import dayjs from '~/timeInit';

export const CAMPAIGN_TIMEZONE = 'Australia/Sydney';

/** Index of the applications open/close term in the `terms` array. */
export const APPLICATION_PERIOD_TERM_INDEX = 1;

const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

/** Today 12:01am AEDT → one month later 11:59pm AEDT (for datetime-local inputs). */
export function getDefaultApplicationPeriod(): { from: string; to: string } {
  const today = dayjs().tz(CAMPAIGN_TIMEZONE);
  const from = today.startOf('day').hour(0).minute(1);
  const to = today.add(1, 'month').startOf('day').hour(23).minute(59);

  return {
    from: from.format(DATETIME_LOCAL_FORMAT),
    to: to.format(DATETIME_LOCAL_FORMAT),
  };
}

function formatTermTime(date: dayjs.Dayjs): string {
  return `${date.format('h:mm')}${date.format('a').toLowerCase()}`;
}

function formatTermDateTime(date: dayjs.Dayjs): string {
  return `${date.format('D MMMM YYYY')} ${formatTermTime(date)} AEDT`;
}

function parseDatetimeLocalInSydney(value: string): dayjs.Dayjs {
  const [datePart, timePart] = value.split('T');
  return dayjs.tz(`${datePart} ${timePart}`, 'YYYY-MM-DD HH:mm', CAMPAIGN_TIMEZONE);
}

export function buildApplicationPeriodTerm(fromInput: string, toInput: string): string {
  const from = parseDatetimeLocalInSydney(fromInput);
  const to = parseDatetimeLocalInSydney(toInput);

  if (!from.isValid() || !to.isValid()) {
    const defaults = getDefaultApplicationPeriod();
    return buildApplicationPeriodTerm(defaults.from, defaults.to);
  }

  return `Applications are open from ${formatTermDateTime(from)} to ${formatTermDateTime(
    to,
  )} ("Challenge Period"). No applications will be accepted after this date.`;
}

export function syncApplicationPeriodTerm(terms: Array<string>, fromInput: string, toInput: string): Array<string> {
  const next = [...terms];
  while (next.length <= APPLICATION_PERIOD_TERM_INDEX) {
    next.push('');
  }
  next[APPLICATION_PERIOD_TERM_INDEX] = buildApplicationPeriodTerm(fromInput, toInput);
  return next;
}

function parseTermDateSegment(segment: string): string | null {
  const cleaned = segment.replace(/\s+AEDT$/i, '').trim();
  const formats = ['D MMMM YYYY h:mma', 'DD/MM/YYYY h:mma'];

  for (const format of formats) {
    const parsed = dayjs(cleaned, format, true);
    if (parsed.isValid()) {
      // Term copy uses Australia/Sydney wall-clock times; keep components as-is for datetime-local inputs.
      return parsed.format(DATETIME_LOCAL_FORMAT);
    }
  }

  return null;
}

/** Parses the applications period term back into datetime-local values, if possible. */
export function parseApplicationPeriodFromTerm(term: string): { from: string; to: string } | null {
  const match = term.match(/Applications are open from (.+?) to (.+?) \("Challenge Period"\)/);
  if (!match) return null;

  const from = parseTermDateSegment(match[1]);
  const to = parseTermDateSegment(match[2]);

  if (!from || !to) return null;
  return { from, to };
}

export function createDefaultApplicationPeriodTerms(baseTerms: Array<string>): Array<string> {
  const { from, to } = getDefaultApplicationPeriod();
  return syncApplicationPeriodTerm([...baseTerms], from, to);
}
